import {
  flip, offset, shift,
} from '@floating-ui/react-dom';
import {
  Navbar2Element,
  NavbarBrand,
  NavbarBrandLogo,
  NavbarButton,
  NavbarItem,
} from '@typedream/core';
import {
  findNodePath, toDOMNode, TNode, getNode,
} from '@udecode/plate-core';
import { useMachine } from '@xstate/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { assign } from 'xstate';
import { Floating } from '../../../../components/Floating';
import { menuOptionsMachine } from '../../../../nodes/navbar/fsm/MenuOptions.fsm';
import {
  changeNavbar,
  changeNavbarLogo,
} from '../../../../nodes/navbar/transformers';
import { OverlayPortal } from '../../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../../plate';
import { TextEditorToogleNavbarEvent } from '../../../../types';
import { NavbarSettingToolbar } from '../../../Navbar/NavbarSettingToolbar/NavbarSettingToolbar';
import toolbarStyles from './NavbarToolbar.module.css';
import { updateArrayItemByPosition, updateMenuItemByType } from './utils';
import { NavbarBrandOverlayToolbar } from './NavbarBrandOverlayToolbar';

export function NavbarMenuItemOverlayToolbar() {
  const editable = true;
  const editor = useTPlateEditorRef();

  const [navbar, setNavbar] = useState<Navbar2Element | undefined>();
  const [domNode, setDomNode] = useState<HTMLElement | undefined>();

  const [machineState, machineSend, machineActor] = useMachine(menuOptionsMachine, {
    actions: {
      cleanUpContext: assign((context) => {
        if (context.dataNavbar) {
          return {
            dataNavbar: undefined,
            menuType: undefined,
            menuIndex: undefined,
          };
        }
        return {};
      }),
      deleteMenuFromContext: assign((context, event) => {
        if (
          context?.dataNavbar
          && event.menuType !== 'brand'
        ) {
          const newData: {
            menu?: Navbar2Element['menu'],
            endMenu?: Navbar2Element['endMenu'],
          } = {
            [event.menuType]: context.dataNavbar[event.menuType].filter(
              (_, index) => index !== event.menuIndex,
            ),
          };

          // Tell other FSM that this menu is deleted
          editor.emitter.emit('toolbar:navbar:menu:delete');

          return {
            dataNavbar: {
              ...context.dataNavbar,
              ...newData,
            },
          };
        }

        return {};
      }),
      effectUpdateSlateNode: (context) => {
        const nodeReference = findNodePath(editor, navbar as TNode);
        changeNavbar(editor, nodeReference!, context.dataNavbar);
      },
      assignLinkToContext: assign((context, event) => {
        // Because of our TS Type, we have to make sure that
        // the `navbar` exist to make it able to destructure
        if (context.dataNavbar && context.menuType === 'brand') {
          const newData: Pick<NavbarBrand, 'logoURL' | 'logoURLInternalLink' | 'logoURLPageID' | 'logoOpenInNewTab'> = {};

          if (event.type === 'ON_CHANGE_LINK') {
            newData.logoURL = event.newLink.pageID ? `/${event.newLink.pagePath}` : event.newLink.url;
            newData.logoURLInternalLink = event.newLink?.pageID ? true : undefined;
            newData.logoURLPageID = event.newLink.pageID;
          }

          if (event.type === 'ON_CHANGE_LINK_OPEN_NEW_TAB') {
            newData.logoOpenInNewTab = event.newLinkOpenInNewTab;
          }

          return {
            dataNavbar: {
              ...context.dataNavbar,
              ...newData,
            },
          };
        }

        if (
          context.dataNavbar
          && context.menuType === 'menu'
        ) {
          const newData: Pick<NavbarItem, 'openInNewTab' | 'pageId' | 'url' | 'internalLink'> = {};

          if (event.type === 'ON_CHANGE_LINK') {
            newData.url = event.newLink.pageID ? `/${event.newLink.pagePath}` : event.newLink.url;
            newData.pageId = event.newLink.pageID;
            newData.internalLink = event.newLink.pageID ? true : undefined;
          }

          if (event.type === 'ON_CHANGE_LINK_OPEN_NEW_TAB') {
            newData.openInNewTab = event.newLinkOpenInNewTab;
          }

          const newMiddleMenuItem: NavbarItem = {
            ...context?.dataNavbar?.menu[context?.menuIndex!],
            ...newData,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );
          return {
            dataNavbar: {
              ...context.dataNavbar,
              menu: newMiddleMenuArr,
            },
          };
        }

        if (
          context.dataNavbar
          && context.menuType === 'endMenu'
        ) {
          const newData: Pick<NavbarItem, 'openInNewTab' | 'pageId' | 'url' | 'internalLink'> = {};

          if (event.type === 'ON_CHANGE_LINK') {
            newData.url = event.newLink.pageID ? `/${event.newLink.pagePath}` : event.newLink.url;
            newData.pageId = event.newLink.pageID;
            newData.internalLink = event.newLink.pageID ? true : undefined;
          }

          if (event.type === 'ON_CHANGE_LINK_OPEN_NEW_TAB') {
            newData.openInNewTab = event.newLinkOpenInNewTab;
          }

          const newMiddleMenuItem: NavbarItem = {
            ...context?.dataNavbar?.endMenu[context?.menuIndex!],
            ...newData,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.endMenu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );
          return {
            dataNavbar: {
              ...context.dataNavbar,
              endMenu: newMiddleMenuArr,
            },
          };
        }
        return {};
      }),
      // TODO(@ibedwi): delete this, just use effect update slate node
      effectUpdateSlateNodeLink: (context) => {
        const nodeReference = findNodePath(editor, navbar as TNode);
        changeNavbar(editor, nodeReference!, context?.dataNavbar);
      },
      // Currently, this line will return type error for `logoType` icon.
      // This may caused by the type of `NavbarBrandLogo`.
      // @ts-ignore
      removeLogoFromContext: assign((context) => {
        if (context.dataNavbar && (context.dataNavbar.logoType === 'image' || context.dataNavbar.logoType === 'icon' || context.dataNavbar?.logoType === 'emoji')) {
          const { logoType, logoImage, ...rest } = context.dataNavbar;
          return {
            dataNavbar: {
              ...rest,
              logoType: '',
            },
          };
        }
        return {};
      }),
      assignLogoToContext: assign((context, event) => {
        if (context.dataNavbar) {
          if (event.type === 'done.invoke.uploadImage') {
            const newLogo: NavbarBrandLogo = {
              logoType: 'image',
              logoImage: event.data,
            };
            return {
              dataNavbar: {
                ...context.dataNavbar,
                ...newLogo,
              },
            };
          }

          if (event.type === 'ON_CHANGE_LOGO') {
            return {
              dataNavbar: {
                ...context.dataNavbar,
                ...event.newLogo,
              },
            };
          }
        }
        return {};
      }),
      // TODO(@ibedwi): delete this, just use effect update slate node
      effectUpdateSlateLogo: (context) => {
        const nodeReference = findNodePath(editor, navbar as TNode);
        changeNavbarLogo(editor, nodeReference!, context?.dataNavbar);
      },
      assignFontToContext: assign((context, event) => {
        // Because of our TS Type, we have to make sure that
        // the `navbar` exist to make it able to destructure
        if (context.dataNavbar && context.menuType === 'brand') {
          return {
            dataNavbar: {
              ...context.dataNavbar,
              logoFontFamily: event.newFont,
            },
          };
        }
        if (context.dataNavbar && context.menuType === 'menu') {
          const newMiddleMenuItem: NavbarItem = {
            ...context?.dataNavbar?.menu[context?.menuIndex!],
            fontFamily: event.newFont,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              menu: newMiddleMenuArr,
            },
          };
        }

        if (context.dataNavbar && context.menuType === 'endMenu') {
          const newMiddleMenuItem: NavbarItem = {
            ...context?.dataNavbar?.endMenu[context?.menuIndex!],
            fontFamily: event.newFont,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.endMenu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              endMenu: newMiddleMenuArr,
            },
          };
        }
        return {};
      }),
      assignTextColor: assign((context, event) => {
        // Because of our TS Type, we have to make sure that
        // the `navbar` exist to make it able to destructure

        if (context.dataNavbar && context.menuType === 'brand') {
          return {
            dataNavbar: {
              ...context.dataNavbar,
              logoColor: event.newColor,
            },
          };
        }

        if (context.dataNavbar && context.menuType === 'menu') {
          const newMiddleMenuItem: NavbarItem = {
            ...context?.dataNavbar?.menu[context?.menuIndex!],
            color: event.newColor,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              menu: newMiddleMenuArr,
            },
          };
        }

        if (context.dataNavbar && context.menuType === 'endMenu') {
          const newMiddleMenuItem: NavbarItem = {
            ...context?.dataNavbar?.endMenu[context?.menuIndex!],
            color: event.newColor,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.endMenu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              endMenu: newMiddleMenuArr,
            },
          };
        }

        return {};
      }),
      assignBackgroundColor: assign((context, event) => {
        const { menuType, dataNavbar, menuIndex } = context;

        if (menuType !== 'brand' && menuType && dataNavbar && menuIndex !== undefined && dataNavbar[menuType][menuIndex].type === 'button') {
          const newMenuItem: NavbarItem = {
            ...dataNavbar[menuType][menuIndex],
            background: event.background,
          } as NavbarButton;

          const newMenuArr = updateArrayItemByPosition(
            dataNavbar[menuType],
            menuIndex ?? -1,
            newMenuItem,
          );

          return {
            dataNavbar: {
              ...dataNavbar,
              [menuType]: newMenuArr,
            },
          };
        }

        return {};
      }),
      assignNavbarToContext: assign((_context, event) => {
        if (event.type === 'ON_CLICK_ITEM') {
          return {
            dataNavbar: {
              ...event.newData.newNavbar,
            },
            menuType: event.newData.menuType,
            menuIndex: event.newData.menuIndex,
          };
        }

        if (event.type === 'ON_CLICK_LOGO_ELEMENT') {
          return {
            dataNavbar: {
              ...event.newData.newNavbar,
            },
            menuType: event.newData.menuType,
          };
        }

        return {};
      }),
      assignTitleToContext: assign((context, event) => {
        // Because of our TS Type, we have to make sure that
        // the `navbar` exist to make it able to destructure
        if (context.dataNavbar && context.menuType === 'brand') {
          return {
            dataNavbar: {
              ...context.dataNavbar,
              logoTitle: event.newTitle,
            },
          };
        }

        if (
          context.dataNavbar
          && context.menuType === 'menu'
          && context.menuIndex !== undefined) {
          const currentMiddleMenuItem = context.dataNavbar.menu[context.menuIndex];
          const newMiddleMenuItem = {
            ...currentMiddleMenuItem,
            title: event.newTitle,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              menu: newMiddleMenuArr,
            },
          };
        }

        if (
          context.dataNavbar
          && context.menuType === 'endMenu'
          && context.menuIndex !== undefined) {
          const currentMiddleMenuItem = context.dataNavbar.endMenu[context.menuIndex];
          const newMiddleMenuItem = {
            ...currentMiddleMenuItem,
            title: event.newTitle,
          };

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.endMenu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          return {
            dataNavbar: {
              ...context.dataNavbar,
              endMenu: newMiddleMenuArr,
            },
          };
        }

        return {};
      }),
      assignLogoSizeToContext: assign((context, event) => {
        if (context.dataNavbar) {
          return {
            dataNavbar: {
              ...context.dataNavbar,
              logoScale: event.newLogoScale,
            },
          };
        }
        return {};
      }),
      assignMenuNodeRef: assign((_context, event) => ({
        dataNavbarNodeReference: event.newMenuRef,
      })),
      assignMenuTypeToContext: assign((context, event) => {
        if (context.dataNavbar && context.menuType === 'menu') {
          const currentMiddleMenuItem = context.dataNavbar.menu[context.menuIndex!];

          // if there's no change in the type, don't do anything
          if (currentMiddleMenuItem.type === event.newMenuItemType) return {};

          const newMiddleMenuItem = updateMenuItemByType(
            currentMiddleMenuItem,
            event.newMenuItemType,
          );

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.menu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          editor.emitter.emit('toolbar:navbar:menu:update');

          return {
            dataNavbar: {
              ...context.dataNavbar,
              menu: newMiddleMenuArr,
            },
          };
        }

        if (context.dataNavbar && context.menuType === 'endMenu') {
          const currentMiddleMenuItem = context.dataNavbar.endMenu[context.menuIndex!];

          // if there's no change in the type, don't do anything
          if (currentMiddleMenuItem.type === event.newMenuItemType) return {};

          const newMiddleMenuItem = updateMenuItemByType(
            currentMiddleMenuItem,
            event.newMenuItemType,
          );

          const newMiddleMenuArr = updateArrayItemByPosition(
            context.dataNavbar.endMenu,
            context.menuIndex ?? -1,
            newMiddleMenuItem,
          );

          editor.emitter.emit('toolbar:navbar:menu:update');

          return {
            dataNavbar: {
              ...context.dataNavbar,
              endMenu: newMiddleMenuArr,
            },
          };
        }

        return {};
      }),
    },
    services: {
      uploadImage: async (_, event) => {
        const url = await editor!.fileManager!.upload(event.newFile);
        return url;
      },
    },
  });

  const handler = (data: TextEditorToogleNavbarEvent) => {
    const { type, navbarPath } = data;

    const navbarFromEmit = getNode<Navbar2Element>(editor, navbarPath);
    if (!navbarFromEmit) return;
    const navbarDOMNode = toDOMNode(editor, navbarFromEmit);

    if (type === 'brand') {
      const { ref } = data;
      setDomNode(ref!);
      setNavbar(navbarFromEmit);
    } else {
      const { ref } = data;
      setDomNode(ref!);
      setNavbar(navbarFromEmit);
    }

    if (navbarDOMNode) {
      const isMenuItem = data.type === 'menu' || data.type === 'endMenu';

      machineSend({
        type: 'ON_CLICK_ITEM',
        newMenuRef: navbarPath!,
        newData: {
          newNavbar: navbarFromEmit,
          menuType: type,
          menuIndex: isMenuItem ? data.index : undefined,
        },
      });
    }
  };

  const navbarMenuLogoEmitHandler = (navbarFromEmit: Navbar2Element) => {
    const navbarDOMNode = toDOMNode(editor, navbarFromEmit);

    const navbarPath = findNodePath(editor, navbarFromEmit);

    if (navbarDOMNode) {
      setDomNode(navbarDOMNode);
      setNavbar(navbarFromEmit);
      machineSend({ type: 'ON_CLICK_LOGO_ELEMENT', newMenuRef: navbarPath!, newData: { newNavbar: navbarFromEmit, menuType: 'brand' } });
    }
  };

  useEffect(() => {
    editor.emitter.on('toolbar:navbar:setting-menu:toggle', handler);
    editor.emitter.on('toolbar:navbar:setting-menu:toggle-logo', navbarMenuLogoEmitHandler);
    return () => {
      editor.emitter.off('toolbar:navbar:setting-menu:toggle', handler);
      editor.emitter.off('toolbar:navbar:setting-menu:toggle-logo', navbarMenuLogoEmitHandler);
    };
  }, [editor.emitter, navbar]);

  return (
    <>
      <div />
      {
        /**
         * This overlay will be drawn in the Editor's canvas iframe
         */
        editable
        && domNode
        && machineState.hasTag('ui:show_setting_menu')
        && (
          <OverlayPortal>
            <Floating
              reference={domNode}
              options={{
                placement: 'bottom',
                middleware: [
                  offset(-6),
                  flip(),
                  shift({
                    crossAxis: true,
                    padding: { top: 8, bottom: 8 },
                  }),
                ],
              }}
              autoUpdate
              className={
                classNames(
                  toolbarStyles.box,
                  // Prevent this portal from being hidden by
                  // the Navbar's
                  toolbarStyles.navbarMenu,
                )
              }
            >
              <NavbarSettingToolbar
                machine={machineActor}
                width={300}
              />
            </Floating>
          </OverlayPortal>
        )
      }
      {
        /**
         * This overlay will be drawn in the Editor's canvas iframe
         */
        editable
        && domNode
        && machineState.hasTag('ui:show_logo_select_media')
        && (
          <OverlayPortal>
            <Floating
              reference={domNode}
              options={{
                placement: 'bottom',
                middleware: [
                  offset(-6),
                  flip(),
                  shift({
                    crossAxis: true,
                    padding: { top: 8, bottom: 8 },
                  }),
                ],
              }}
              autoUpdate
              className={
                classNames(
                  toolbarStyles.box,
                  // Prevent this portal from being hidden by
                  // the Navbar's
                  toolbarStyles.navbarMenu,
                )
              }
            >
              <NavbarBrandOverlayToolbar machineActor={machineActor} />
            </Floating>
          </OverlayPortal>
        )
      }
    </>
  );
}
